/**
 * PromoCodeUsageSearch - Admin Portal Page
 * 
 * Search and manage promo code usage by code, email, or user ID.
 * Provides detailed redemption history and admin actions.
 */

import React, { useState } from 'react';
import ResetUsageModal from '../components/modals/ResetUsageModal';
import RegenerateCodeModal from '../components/modals/RegenerateCodeModal';
import FlagFraudModal from '../components/modals/FlagFraudModal';
import './PromoCodeUsageSearch.css';

const LAMBDA_URL = 'https://idnyqqvznbjt7w2hhtm3htpqhu0frkbg.lambda-url.ap-southeast-1.on.aws';

const PromoCodeUsageSearch = () => {
  const [searchType, setSearchType] = useState('code');
  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal states
  const [selectedCode, setSelectedCode] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showFlagFraudModal, setShowFlagFraudModal] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchValue.trim()) {
      setError('Please enter a search value');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await fetch(`${LAMBDA_URL}/search?${searchType}=${encodeURIComponent(searchValue.trim())}`);
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search promo codes');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'status-badge status-active',
      exhausted: 'status-badge status-exhausted',
      expired: 'status-badge status-expired'
    };
    return <span className={statusClasses[status] || 'status-badge'}>{status}</span>;
  };

  const getPromoTypeLabel = (promoType) => {
    const labels = {
      lifetime_vip: 'Lifetime VIP',
      annual_trial: 'Annual Trial',
      trial_extension: 'Trial Extension',
      seasonal_discount: 'Seasonal Discount'
    };
    return labels[promoType] || promoType;
  };

  const exportToCSV = () => {
    if (!results || !results.results || results.results.length === 0) return;

    const rows = [];
    rows.push(['Code', 'Type', 'Status', 'User Email', 'User ID', 'Redeemed At', 'Plan', 'Trial Days']);

    results.results.forEach(code => {
      code.redemptions.forEach(redemption => {
        rows.push([
          code.code,
          getPromoTypeLabel(code.promoType),
          code.status,
          redemption.userEmail,
          redemption.userId,
          formatDate(redemption.redeemedAt),
          redemption.planSelected || 'unknown',
          redemption.trialDays || 'N/A'
        ]);
      });
    });

    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `promo-code-usage-${searchType}-${searchValue}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Modal handlers
  const handleOpenResetModal = (code) => {
    setSelectedCode(code);
    setShowResetModal(true);
  };

  const handleOpenRegenerateModal = (code) => {
    setSelectedCode(code);
    setShowRegenerateModal(true);
  };

  const handleOpenFlagFraudModal = (code) => {
    setSelectedCode(code);
    setShowFlagFraudModal(true);
  };

  const handleModalSuccess = (updatedCode) => {
    // Refresh search results
    handleSearch({ preventDefault: () => {} });
  };

  return (
    <div className="promo-usage-search">
      <div className="promo-usage-search__header">
        <h2 className="promo-usage-search__title">Promo Code Usage Search</h2>
        <p className="promo-usage-search__description">
          Search for promo codes by code, user email, or user ID to view usage details and make informed decisions.
        </p>
      </div>

      <form className="promo-usage-search__form" onSubmit={handleSearch}>
        <div className="promo-usage-search__form-row">
          <div className="promo-usage-search__search-type">
            <label className="promo-usage-search__label">Search By:</label>
            <div className="promo-usage-search__radio-group">
              <label className="promo-usage-search__radio">
                <input
                  type="radio"
                  value="code"
                  checked={searchType === 'code'}
                  onChange={(e) => setSearchType(e.target.value)}
                />
                <span>Promo Code</span>
              </label>
              <label className="promo-usage-search__radio">
                <input
                  type="radio"
                  value="email"
                  checked={searchType === 'email'}
                  onChange={(e) => setSearchType(e.target.value)}
                />
                <span>User Email</span>
              </label>
              <label className="promo-usage-search__radio">
                <input
                  type="radio"
                  value="userId"
                  checked={searchType === 'userId'}
                  onChange={(e) => setSearchType(e.target.value)}
                />
                <span>User ID</span>
              </label>
            </div>
          </div>

          <div className="promo-usage-search__input-group">
            <input
              type="text"
              className="promo-usage-search__input"
              placeholder={
                searchType === 'code' ? 'Enter promo code (e.g., EDUQ-4SME-UA7G)' :
                searchType === 'email' ? 'Enter user email' :
                'Enter user ID (Cognito sub)'
              }
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              disabled={loading}
            />
            <button
              type="submit"
              className="promo-usage-search__button"
              disabled={loading || !searchValue.trim()}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div className="promo-usage-search__error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {results && (
        <div className="promo-usage-search__results">
          <div className="promo-usage-search__results-header">
            <h3 className="promo-usage-search__results-title">
              Search Results
              {results.totalCodes && <span className="promo-usage-search__count"> ({results.totalCodes} code{results.totalCodes !== 1 ? 's' : ''})</span>}
            </h3>
            {results.results && results.results.length > 0 && (
              <button
                className="promo-usage-search__export-button"
                onClick={exportToCSV}
              >
                Export to CSV
              </button>
            )}
          </div>

          {results.message && results.results.length === 0 ? (
            <div className="promo-usage-search__no-results">
              {results.message}
            </div>
          ) : (
            <div className="promo-usage-search__cards">
              {results.results.map((code, index) => (
                <div key={index} className="promo-code-card">
                  <div className="promo-code-card__header">
                    <div className="promo-code-card__code-info">
                      <h4 className="promo-code-card__code">{code.code}</h4>
                      <span className="promo-code-card__type">{getPromoTypeLabel(code.promoType)}</span>
                    </div>
                    {getStatusBadge(code.status)}
                  </div>

                  <div className="promo-code-card__meta">
                    <div className="promo-code-card__meta-item">
                      <span className="promo-code-card__meta-label">Usage:</span>
                      <span className="promo-code-card__meta-value">
                        {code.usageCount} / {code.maxUsage}
                      </span>
                    </div>
                    <div className="promo-code-card__meta-item">
                      <span className="promo-code-card__meta-label">Expires:</span>
                      <span className="promo-code-card__meta-value">
                        {formatDate(code.expiryDate)}
                      </span>
                    </div>
                    {code.createdBy && (
                      <div className="promo-code-card__meta-item">
                        <span className="promo-code-card__meta-label">Created By:</span>
                        <span className="promo-code-card__meta-value">{code.createdBy}</span>
                      </div>
                    )}
                  </div>

                  {code.notes && (
                    <div className="promo-code-card__notes">
                      <strong>Notes:</strong> {code.notes}
                    </div>
                  )}

                  {code.redemptions && code.redemptions.length > 0 && (
                    <div className="promo-code-card__redemptions">
                      <h5 className="promo-code-card__redemptions-title">
                        Redemptions ({code.redemptions.length})
                      </h5>
                      {code.redemptions.map((redemption, rIndex) => (
                        <div key={rIndex} className="redemption-item">
                          <div className="redemption-item__user">
                            <div className="redemption-item__email">{redemption.userEmail}</div>
                            <div className="redemption-item__user-id">{redemption.userId}</div>
                          </div>
                          <div className="redemption-item__details">
                            <div className="redemption-item__detail">
                              <span className="redemption-item__label">Redeemed:</span>
                              <span className="redemption-item__value">{formatDate(redemption.redeemedAt)}</span>
                            </div>
                            <div className="redemption-item__detail">
                              <span className="redemption-item__label">Plan:</span>
                              <span className="redemption-item__value">
                                {redemption.planSelected || 'unknown'}
                                {redemption.planSelected === 'unknown' && (
                                  <span className="redemption-item__warning"> (Payment incomplete)</span>
                                )}
                              </span>
                            </div>
                            <div className="redemption-item__detail">
                              <span className="redemption-item__label">Trial Days:</span>
                              <span className="redemption-item__value">{redemption.trialDays || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="promo-code-card__actions">
                    <button 
                      className="promo-code-card__action-button promo-code-card__action-button--regenerate"
                      onClick={() => handleOpenRegenerateModal(code)}
                    >
                      Regenerate Code
                    </button>
                    <button 
                      className="promo-code-card__action-button promo-code-card__action-button--reset"
                      onClick={() => handleOpenResetModal(code)}
                    >
                      Reset Usage
                    </button>
                    <button 
                      className="promo-code-card__action-button promo-code-card__action-button--flag"
                      onClick={() => handleOpenFlagFraudModal(code)}
                    >
                      Flag as Fraud
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {selectedCode && (
        <>
          <ResetUsageModal
            isOpen={showResetModal}
            onClose={() => setShowResetModal(false)}
            code={selectedCode}
            onSuccess={handleModalSuccess}
          />
          <RegenerateCodeModal
            isOpen={showRegenerateModal}
            onClose={() => setShowRegenerateModal(false)}
            code={selectedCode}
            onSuccess={handleModalSuccess}
          />
          <FlagFraudModal
            isOpen={showFlagFraudModal}
            onClose={() => setShowFlagFraudModal(false)}
            code={selectedCode}
            onSuccess={handleModalSuccess}
          />
        </>
      )}
    </div>
  );
};

export default PromoCodeUsageSearch;
